import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronLeft } from '@fortawesome/free-solid-svg-icons';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);

  const updateHtmlLang = useCallback((lang: string) => {
    const htmlLang = lang === 'cz' ? 'cs' : lang;
    document.documentElement.lang = htmlLang;
  }, []);

  const debouncedChangeLanguage = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
    updateHtmlLang(lang);
    const path = location.pathname;
    const pathParts = path.split('/').filter(Boolean);
    
    if (lang === 'cz') {
      const newPath = pathParts.length > 1 ? `/${pathParts.slice(1).join('/')}` : '/';
      navigate(newPath);
    } else {
      const newPath = pathParts.length > 1 ? `/${lang}/${pathParts.slice(1).join('/')}` : `/${lang}`;
      navigate(newPath);
    }
  }, [i18n, location.pathname, navigate, updateHtmlLang]);

  const changeLanguage = useCallback((lang: string) => {
    debouncedChangeLanguage(lang);
  }, [debouncedChangeLanguage]);

  useEffect(() => {
    if (isInitialized) return;

    const path = location.pathname;
    const pathLang = path.split('/')[1];

    if (pathLang && ['cz', 'en', 'sk'].includes(pathLang)) {
      i18n.changeLanguage(pathLang);
      updateHtmlLang(pathLang);
      setIsInitialized(true);
      return;
    }

    const browserLang = navigator.language.toLowerCase();
    let detectedLang = 'en';

    if (browserLang.startsWith('cs')) {
      detectedLang = 'cz';
    } else if (browserLang.startsWith('sk')) {
      detectedLang = 'sk';
    }

    if (detectedLang !== i18n.language) {
      debouncedChangeLanguage(detectedLang);
    }
    
    setIsInitialized(true);
  }, [location.pathname, i18n, debouncedChangeLanguage, isInitialized, updateHtmlLang]);

  return (
    <div className="flex items-center justify-center-safe md:flex-row lg:flex-col xl:flex-row">
      <div className="relative">
        <button
          onClick={() => changeLanguage('cz')}
          className="w-10 h-10 text-2xl leading-none flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110"
          title="Czech"
        >
          🇨🇿
        </button>
        {i18n.language === 'cz' && (
          <>
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 xl:block lg:hidden block">
              <FontAwesomeIcon icon={faChevronUp} className="text-xs text-primary" />
            </div>
            <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 xl:hidden lg:block md:hidden hidden">
              <FontAwesomeIcon icon={faChevronLeft} className="text-xs text-primary" />
            </div>
          </>
        )}
      </div>
      <div className="relative">
        <button
          onClick={() => changeLanguage('en')}
          className="w-10 h-10 text-2xl leading-none flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110"
          title="English"
        >
          🇬🇧
        </button>
        {i18n.language === 'en' && (
          <>
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 xl:block lg:hidden block">
              <FontAwesomeIcon icon={faChevronUp} className="text-xs text-primary" />
            </div>
            <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 xl:hidden lg:block md:hidden hidden">
              <FontAwesomeIcon icon={faChevronLeft} className="text-xs text-primary" />
            </div>
          </>
        )}
      </div>
      <div className="relative">
        <button
          onClick={() => changeLanguage('sk')}
          className="w-10 h-10 text-2xl leading-none flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110"
          title="Slovak"
        >
          🇸🇰
        </button>
        {i18n.language === 'sk' && (
          <>
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 xl:block lg:hidden block">
              <FontAwesomeIcon icon={faChevronUp} className="text-xs text-primary" />
            </div>
            <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 xl:hidden lg:block md:hidden hidden">
              <FontAwesomeIcon icon={faChevronLeft} className="text-xs text-primary" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LanguageSwitcher; 
